export interface PayrollSetting {
  id: string;
  key: string;
  value: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePayrollSettingDto {
  key: string;
  value: string;
  description?: string;
}

export interface UpdatePayrollSettingDto {
  key?: string;
  value?: string;
  description?: string;
}

export interface UpsertPayrollSettingDto {
  value: string;
  description?: string;
}

export interface PayrollSettings {
  payPeriod: string;
  paymentDay: string;
  weekStartDay: string;
  weekEndDay: string;
  overtimeThreshold: string;
  overtimeMultiplier: string;
}

export enum PayrollInvoiceStatus {
  DRAFT = 'draft',
  PENDING = 'pending',
  APPROVED = 'approved',
  PAID = 'paid',
  CANCELLED = 'cancelled',
}

export enum PayrollPeriodType {
  WEEKLY = 'weekly',
  BIWEEKLY = 'biweekly',
  MONTHLY = 'monthly',
}

export interface PayrollInvoice {
  id: string;
  invoiceNumber: string;
  staffId: string;
  createdById?: string;
  approvedById?: string;
  periodStart: string;
  periodEnd: string;
  periodType: PayrollPeriodType;
  regularHours: number;
  overtimeHours: number;
  regularRate: number;
  overtimeRate: number;
  regularPay: number;
  overtimePay: number;
  bonuses: number;
  deductions: number;
  grossPay: number;
  netPay: number;
  status: PayrollInvoiceStatus;
  approvedAt?: string;
  paidAt?: string;
  notes?: string;
  breakdown?: {
    workHours: { date: string; hours: number }[];
    overtimeWorkHours: { date: string; hours: number }[];
    bonusItems: { description: string; amount: number }[];
    deductionItems: { description: string; amount: number }[];
  };
  metadata?: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  staff?: {
    id: string;
    user: {
      firstName: string;
      lastName: string;
      email: string;
    };
    staffRef: string;
  };
  createdBy?: {
    firstName: string;
    lastName: string;
  };
  approvedBy?: {
    firstName: string;
    lastName: string;
  };
  workSessions?: WorkSession[];
}

export interface WorkSession {
  id: string;
  staffId: string;
  payrollInvoiceId?: string;
  clockIn: string;
  clockOut?: string;
  activity?: string;
  duration?: number;
  hourlyRate?: number;
  isOvertime: boolean;
  isPaid: boolean;
}

export interface CreatePayrollInvoiceDto {
  staffId: string;
  periodStart: string;
  periodEnd: string;
  periodType?: PayrollPeriodType;
  regularHours?: number;
  overtimeHours?: number;
  regularRate?: number;
  overtimeRate?: number;
  bonuses?: number;
  deductions?: number;
  status?: PayrollInvoiceStatus;
  notes?: string;
  workSessionIds?: string[];
  autoCalculate?: boolean;
}

export interface UpdatePayrollInvoiceDto {
  staffId?: string;
  periodStart?: string;
  periodEnd?: string;
  periodType?: PayrollPeriodType;
  regularHours?: number;
  overtimeHours?: number;
  regularRate?: number;
  overtimeRate?: number;
  bonuses?: number;
  deductions?: number;
  status?: PayrollInvoiceStatus;
  notes?: string;
  workSessionIds?: string[];
  autoCalculate?: boolean;
}

export interface PayrollQueryDto {
  page?: number;
  limit?: number;
  staffId?: string;
  status?: PayrollInvoiceStatus;
  periodType?: PayrollPeriodType;
  startDate?: string;
  endDate?: string;
  search?: string;
}

export interface PayrollDashboardData {
  totalDraft: number;
  totalPending: number;
  totalApproved: number;
  totalPaid: number;
  totalAmount: number;
  recentInvoices: PayrollInvoice[];
}
